import { useCallback, useEffect, useState } from "react";
import { useNotify } from "react-admin";

import { logError } from "../../../utils/appUtils";
import { GateCheckInStatus } from "../../../utils/constants";
import { applyStopRules } from "../../../utils/driverStops";
import { sessionYardDriverStops } from "../../../utils/gateAccess";
import {
  GateCheckInConflictError,
  GateDepartureNotDockedError,
  GateDepartureWorkPendingError,
  GateQueueOrderError,
  saveGateCheckIn,
} from "./gateCheckInUtils";
import { GatePlacementBlockedError } from "./gatePlacement";

const { ARRIVED, CALLED, DOCKED } = GateCheckInStatus;

/**
 * Edit mechanics for one Gate Queue row. The shipment dispatch tabs used to
 * share this editor; they now stage their pending edit in the shipment form
 * (gateCheckInEdit) so the form's primary Save persists it — this hook backs
 * the queue page only.
 *
 * @param checkIn the row being edited (null renders nothing upstream)
 * @param onSaved called with the freshly saved row
 * @param onReload refetches the row after a conflict, so the user retries
 *   against what the gate actually recorded
 */
export const useGateCheckInEditor = (checkIn, { onSaved, onReload } = {}) => {
  const notify = useNotify();
  const [pendingStatus, setPendingStatus] = useState(null);
  const [pendingDock, setPendingDock] = useState(null);
  const [pendingDriver, setPendingDriver] = useState(null);
  const [pendingPlate, setPendingPlate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(false);

  // Clear pending edits when the row identity changes, never on a poll tick —
  // a background refresh must not wipe what the user is part way through
  // typing into the dock field.
  useEffect(() => {
    setPendingStatus(null);
    setPendingDock(null);
    setPendingDriver(null);
    setPendingPlate(null);
    setConflict(false);
  }, [checkIn?.id]);

  const status = pendingStatus ?? checkIn?.status ?? "";
  const dock = pendingDock ?? checkIn?.dockAssignment ?? "";
  const driverName = pendingDriver ?? checkIn?.driverName ?? "";
  const truckPlate = pendingPlate ?? checkIn?.truckPlate ?? "";
  const dirty =
    Boolean(checkIn) &&
    (status !== checkIn.status ||
      dock !== (checkIn.dockAssignment || "") ||
      driverName !== (checkIn.driverName || "") ||
      truckPlate !== (checkIn.truckPlate || ""));

  const save = useCallback(async () => {
    if (!checkIn) return;
    setSaving(true);
    try {
      const saved = await saveGateCheckIn(checkIn, {
        status: pendingStatus || undefined,
        dockAssignment: pendingDock !== null ? pendingDock : undefined,
        driverName: pendingDriver !== null ? pendingDriver : undefined,
        truckPlate: pendingPlate !== null ? pendingPlate : undefined,
      });
      setPendingStatus(null);
      setPendingDock(null);
      setPendingDriver(null);
      setPendingPlate(null);
      // At the dock means the load is spotted: saveGateCheckIn placed the
      // shipment before it wrote the row, so a save that landed DOCKED from
      // anywhere else has both halves done (no form is open here to stage
      // the stamp in).
      notify(
        saved.status === DOCKED && checkIn.status !== DOCKED
          ? "Driver docked and shipment placed"
          : "Check-in updated",
      );
      if (onSaved) onSaved(saved);
    } catch (err) {
      if (err instanceof GatePlacementBlockedError) {
        // The shipment cannot be placed, so the driver was not docked — the
        // row is unchanged and the pending pick stands. Not a retry from
        // here: the fix is on the shipment (its carrier, trailer or
        // inventory), which the message names.
        notify(`${err.message} — the driver was not docked.`, "warning");
      } else if (err instanceof GateCheckInConflictError) {
        setConflict(true);
        notify(
          "This check-in changed at the gate — reload before saving",
          "warning",
        );
      } else if (err instanceof GateQueueOrderError) {
        // The queue moved on since this row was rendered: another truck is now
        // at the front. Not a conflict on this row, so the pending edit stands
        // and the supervisor can save it once their turn comes. Say both — that
        // the status did not change and that the edit can be re-saved when the
        // truck ahead has moved — so the bare tooltip does not read as a status
        // the save accepted.
        notify(
          `${err.message} — the status was not changed. Try again once it is this truck's turn.`,
          "warning",
        );
      } else if (
        err instanceof GateDepartureNotDockedError ||
        err instanceof GateDepartureWorkPendingError
      ) {
        // The select greys DEPARTED out on a row that never docked or whose
        // load is unworked, so this is a row (or load) that moved since it
        // was drawn — or a status set before the rule existed. Not a retry:
        // the message names what has to happen first, and the pending edit
        // stands so the supervisor can change the pick rather than start over.
        notify(`${err.message} — the status was not changed.`, "warning");
      } else {
        logError("useGateCheckInEditor:save", err, notify);
      }
    } finally {
      setSaving(false);
    }
  }, [
    checkIn,
    pendingStatus,
    pendingDock,
    pendingDriver,
    pendingPlate,
    notify,
    onSaved,
  ]);

  // The stop route couples the two pending fields the way the write will
  // (applyStopRules in saveGateCheckIn), so what the row shows before Save is
  // what Save writes: picking CALLED fills in the first stop, picking DOCKED
  // the last, and picking a stop for a waiting driver calls them. Clearing
  // the stop that made the call un-calls a driver the gate never called, and
  // so does picking ARRIVED back on a waiting row: the stop the call filled
  // in goes with it, or the write would read that stop as the call and save
  // CALLED under a select that says ARRIVED.
  const setStatus = useCallback(
    (nextStatus) => {
      if (nextStatus === ARRIVED && checkIn?.status === ARRIVED) {
        setPendingStatus(null);
        setPendingDock(null);
        return;
      }
      setPendingStatus(nextStatus);
      const ruled = applyStopRules({
        stops: sessionYardDriverStops(),
        current: checkIn,
        status: nextStatus,
        dockAssignment: dock,
      });
      if (ruled.dockAssignment !== dock) setPendingDock(ruled.dockAssignment);
    },
    [checkIn, dock],
  );
  const setDock = useCallback(
    (nextDock) => {
      setPendingDock(nextDock);
      const ruled = applyStopRules({
        stops: sessionYardDriverStops(),
        current: checkIn,
        status,
        dockAssignment: nextDock,
      });
      if (ruled.status !== status) setPendingStatus(ruled.status);
      else if (
        !nextDock &&
        status === CALLED &&
        checkIn?.status === ARRIVED &&
        pendingStatus === CALLED
      )
        setPendingStatus(null);
    },
    [checkIn, status, pendingStatus],
  );

  const clearConflict = useCallback(() => {
    setPendingStatus(null);
    setPendingDock(null);
    setPendingDriver(null);
    setPendingPlate(null);
    setConflict(false);
    if (onReload) onReload();
  }, [onReload]);

  return {
    status,
    dock,
    driverName,
    truckPlate,
    setStatus,
    setDock,
    setDriverName: setPendingDriver,
    setTruckPlate: setPendingPlate,
    dirty,
    saving,
    conflict,
    clearConflict,
    save,
  };
};
